<script setup lang="ts">
import PHP2xImage from '@/assets/images/prize-cards/php2x.webp'
import { formatDuration } from '@/helpers/date'
import { T } from '@tolgee/vue'
import BaseButton from '@/components/base/BaseButton.vue'
import PrizeCard from '@/components/PrizeCard.vue'
import type { EcommercePrizeCardItem } from '@/types/shop'
import { useEcommercePrice } from '@/composables/game/ecommerce'

defineProps<{
  item: EcommercePrizeCardItem
  onlyVisual?: boolean
  isLastEvenItem?: boolean
  outerHeight?: number
  isLoading?: boolean
}>()

defineEmits<{
  (e: 'heightChange', height: number): void
}>()

const { getStarPrice } = useEcommercePrice()

</script>

<template>
  <PrizeCard
    :class="onlyVisual && 'pointer-events-none'"
    :disabled="item.disabledSeconds > 0 && !onlyVisual"
    :color="item.color"
    :is-large="isLastEvenItem"
    :outer-height="outerHeight"
    @height-change="$emit('heightChange', $event)"
  >
    <div
      class="h-full w-full flex"
      :class="isLastEvenItem ? 'items-center' : 'flex-col items-center'"
    >
      <div
        class="flex flex-col items-center"
        :class="[isLastEvenItem ? 'min-w-[170px] justify-center h-full' : 'flex-1', onlyVisual && 'justify-center']"
      >
        <img
          :src="item.timeImage"
          class="h-6 w-auto object-cover mt-4"
        >
        <div
          class="w-[100px] mt-6"
          :class="{'flex-1': !isLastEvenItem && !onlyVisual}"
        >
          <img
            :src="PHP2xImage"
            class="w-full h-auto"
          >
        </div>
      </div>
      <div
        v-if="!onlyVisual"
        :class="isLastEvenItem ? 'w-[182px] max-w-[182px] flex-1 flex h-full items-center justify-center m-auto' : 'w-full'"
      >
        <BaseButton
          v-if="item.disabledSeconds > 0"
          class="w-full"
          button-class="unslashed-zero px-3 h-[48px] text-base whitespace-nowrap !border-white/40"
          disabled
        >
          Next in {{ formatDuration(item.disabledSeconds) }}
        </BaseButton>
        <BaseButton
          v-else-if="getStarPrice(item)?.amount === 0"
          button-class="px-3 h-[48px] text-base"
          class="w-full"
          :loading="isLoading"
        >
          <T
            key-name="shop.free"
            default-value="Free"
          />
        </BaseButton>
        <div
          v-else
          class="w-full flex relative"
          :class="[isLastEvenItem ? 'black-gradient' : 'rounded-[13px] p-0.5 green-gradient']"
        >
          <BaseButton
            :style-type="isLastEvenItem ? 'dark' : 'green'"
            class="w-full"
            :button-class="`px-3 ${isLastEvenItem ? 'h-[52px]' : 'h-[48px]'} gap-x-1.5 whitespace-nowrap text-base`"
          >
            {{ `$${getStarPrice(item).usdAmount.toFixed(2)}` }}
          </BaseButton>
        </div>
      </div>
    </div>
  </PrizeCard>
</template>
